import type { PanelState } from "@kundeninfo/ui";

/**
 * Der Randzustand liegt im Browser, NICHT in der Datenbank. Grund: Er ist eine
 * geräteabhängige Bequemlichkeit — dasselbe Benutzerkonto sitzt mal am Desktop (wo beide
 * Ränder ohnehin dauerhaft stehen), mal am iPad (wo sie geklappt werden). Ein in der
 * Datenbank gemerkter Zustand würde vom einen Gerät auf das andere überschwappen und dort
 * das Falsche wiederherstellen. Was in der Datenbank steht, ist deshalb nur der Schalter
 * `rememberPanels` selbst — die Absicht des Benutzers, nicht der Zustand.
 */
export const SPEICHER_SCHLUESSEL = "ki-randbereiche";

/**
 * Standardzustand der Ränder, wenn nichts (Brauchbares) gemerkt ist. Er gilt für jede
 * Größenklasse, weil ihn nur eine davon überhaupt auswertet: Auf dem Desktop stehen beide
 * Ränder unabhängig von diesem Wert dauerhaft offen, auf dem Telefon gibt es sie gar nicht.
 * Bleibt das iPad — dort startet der Kontext eingeklappt, weil Navigation, Liste und Kontext
 * zusammen kaum Platz für die Akte lassen (siehe AppShell.css) und der Kontext die am
 * wenigsten dringende der drei Randspalten ist; er lässt sich jederzeit aufklappen.
 */
export const STANDARD_PANELS: PanelState = { nav: true, context: false };

function istPanelState(wert: unknown): wert is PanelState {
  if (typeof wert !== "object" || wert === null) return false;
  const kandidat = wert as { nav?: unknown; context?: unknown };
  return typeof kandidat.nav === "boolean" && typeof kandidat.context === "boolean";
}

/**
 * Der zuletzt gemerkte Randzustand, oder null, wenn keiner vorliegt. Der Wert kommt aus dem
 * Browser: Er kann fehlen, aus einer älteren Fassung stammen oder von Hand verändert worden
 * sein — deshalb wird er geprüft, bevor er zurückgegeben wird, statt blind übernommen.
 *
 * Jeder Zugriff steht in try/catch: In einem privaten Fenster wirft bereits das LESEN
 * (der Zugriff auf `localStorage` selbst, nicht erst getItem). Fehlt der Wert oder wirft
 * der Zugriff, gilt der Standard — das Merken ist eine Bequemlichkeit, kein Muss.
 */
export function lesePanels(): PanelState | null {
  try {
    const roh = localStorage.getItem(SPEICHER_SCHLUESSEL);
    if (roh === null) return null;
    const geparst: unknown = JSON.parse(roh);
    return istPanelState(geparst) ? { nav: geparst.nav, context: geparst.context } : null;
  } catch {
    return null;
  }
}

/** Merkt den Randzustand für den nächsten Start. Scheitert das, bleibt es folgenlos. */
export function merkePanels(panels: PanelState): void {
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify({
      nav: panels.nav, context: panels.context,
    }));
  } catch {
    // Privater Modus oder blockierter Speicher — die Ränder stehen trotzdem richtig,
    // sie werden beim nächsten Start nur nicht wiederhergestellt.
  }
}

/**
 * Verwirft den gemerkten Randzustand. Wird beim Ausschalten des Schalters gerufen, damit
 * ein späteres Einschalten nicht einen längst überholten Zustand wiederbelebt.
 */
export function vergissPanels(): void {
  try {
    localStorage.removeItem(SPEICHER_SCHLUESSEL);
  } catch {
    // siehe merkePanels
  }
}
